import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { IWidgetControllerConfig } from '@wix/native-components-infra/dist/src/types/types';
import type { FulfillmentMethod } from '@wix/ambassador-restaurants-v1-fulfillment-method/types';
import type { TimeSlot as AvailabilityTimeSlot } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { MenuAvailability } from '@wix/restaurants-olo-operations-client-commons';

import {
  getCoverImage,
  type CoverImage,
} from '../../services/coverImageService';
import { operationsService } from '../../services/operationsService';
import { menuService } from '../../services/menuService';
import { itemService } from '../../services/itemService';
import { ordersSettingsService } from '../../services/ordersSettingsService';
import { fulfillmentsService } from '../../services/fulfillmentsService';
import { premiumService } from '../../services/premiumService';
import { getOperationsMock } from '../../editor/editorMocks/getOperationsMock';
import { getMenusMock } from '../../editor/editorMocks/getMenusMock';
import { getItemsMock } from '../../editor/editorMocks/getItemsMock';
import { getMockMenus } from '../../editor/editorMocks/menus';
import {
  getTimeSlotsMock,
  getTimeSlotsPerOperationMock,
} from '../../editor/editorMocks/getTimeSlotsMock';
import { getFulfillmentMethodsMock } from '../../editor/editorMocks/getFulfillmentMethodMocks';
import { getDefaultMockOnlineOrdersStorage } from '../../editor/editorMocks/onlineOrdersStorage';
import { noop } from '../../utils/noop';
import { createStorageProvider } from '../../utils/storageContext';
import { RequestStatus, wrapRequest } from '../../utils/wrapRequest';
import {
  getInstance,
  getRegionalSettings,
  getSiteLocale,
  getSiteCurrency,
  getSiteTimezone,
} from '../../utils/flowAPI';
import { DEFAULT_LOCALE } from '../../utils/constants';
import { getTimezoneOffset } from '../../utils/time';
import {
  getAvailabilityStatusForTimeSlot,
  type ResolvedItemAvailability,
} from '../../utils/menuAvailability';
import {
  DispatchType,
  type Address,
  type DispatchesInfo,
  type TimeSlot,
} from '../../types/businessTypes';
import type {
  MenuAvailabilityStatusById,
  SectionAvailabilityStatusById,
  TimeSlotsPerMenu,
} from '../../types/menuAvailability';
import type { Operation } from '../../types/operation';
import type {
  MenuItem,
  MenuSectionAvailability,
  PopulatedMenu,
} from '../../types/menu';

import type {
  OpenFulfillmentModalParams,
  OpenFulfillmentModalResult,
} from './actions/openFulfillmentModal';

interface OnlineOrdersData {
  regionalSettings?: string;
  siteLocale: string;
  timezone: string;
  isPremium: boolean;
  fetchIsPremiumStatus: RequestStatus;
  fitToContentHeight: boolean;
  operations: Operation[];
  currentOperation?: Operation;
  isLocationSelected: boolean;
  menus: PopulatedMenu[];
  menuAvailabilityById: Record<string, MenuAvailability>;
  menuAvailabilityStatusById: MenuAvailabilityStatusById;
  menuNextAvailabilityById: Record<string, boolean>;
  sectionAvailabilityById: Partial<Record<string, MenuSectionAvailability>>;
  sectionAvailabilityStatusById: SectionAvailabilityStatusById;
  itemMap: Record<string, MenuItem>;
  cartQuantities: Record<string, number>;
  fetchOperationsStatus: RequestStatus;
  fetchMenusStatus: RequestStatus;
  fetchItemsStatus: RequestStatus;
  fulfillmentMethods: FulfillmentMethod[];
  fetchFulfillmentMethodsStatus: RequestStatus;
  selectedDispatchType?: DispatchType;
  currency: string;
  timeSlots: TimeSlot[];
  fetchTimeSlotsStatus: RequestStatus;
  timeSlotsByOperationId: Record<string, TimeSlot[]>;
  fetchTimeSlotsByOperationStatus: RequestStatus;
  acceptingDispatchTypesByOperationId: Record<string, DispatchType[]>;
  dispatchesInfo: Partial<DispatchesInfo>;
  hasError: boolean;
  coverImage?: CoverImage;
  fetchCoverImageStatus: RequestStatus;
}

interface OnlineOrdersActions {
  fetchOperations: (
    operationGroupId?: string,
  ) => Promise<Operation[] | undefined>;
  fetchMenus: (operationId: string) => Promise<PopulatedMenu[] | undefined>;
  getCachedOperationMenus: (operationId: string) => PopulatedMenu[] | undefined;
  updateMenuAvailabilityStatus: (
    operationId: string,
    timeSlot?: AvailabilityTimeSlot,
    options?: {
      dispatchType?: DispatchType;
      deliveryAddress?: Address;
    },
  ) => Promise<PopulatedMenu[] | undefined>;
  fetchItems: (
    itemIds: string[],
  ) => Promise<Record<string, MenuItem> | undefined>;
  fetchFulfillmentMethods: (
    fulfillmentIds: string[],
  ) => Promise<FulfillmentMethod[] | undefined>;
  fetchTimeSlots: (
    operation: Operation,
    deliveryAddress?: Address,
  ) => Promise<TimeSlot[] | undefined>;
  fetchTimeSlotsByOperation: (
    operationIds: string[],
    deliveryAddress?: Address,
  ) => Promise<Record<string, TimeSlot[]> | undefined>;
  fetchIsPremium: () => Promise<boolean | undefined>;
  fetchCoverImage: (operationGroupId: string) => Promise<CoverImage | undefined>;
  setIsLocationSelected: (value: boolean) => void;
  setSelectedDispatchType: (type: DispatchType) => void;
  setDispatchesInfo: (dispatchesInfo: Partial<DispatchesInfo>) => void;
  setAcceptingDispatchTypesByOperationId: (
    map: Record<string, DispatchType[]>,
  ) => void;
  onItemClick: (
    item: MenuItem,
    menuId: string,
    sectionId: string,
    availability?: ResolvedItemAvailability,
  ) => void;
  openCart: () => void;
  openFulfillmentModal: (
    params: OpenFulfillmentModalParams,
  ) => Promise<OpenFulfillmentModalResult | undefined>;
}

export interface OnlineOrdersStorage
  extends OnlineOrdersData,
    OnlineOrdersActions {}

const defaultOnlineOrdersStorage: OnlineOrdersStorage = {
  siteLocale: DEFAULT_LOCALE,
  timezone: '',
  isPremium: false,
  fetchIsPremiumStatus: RequestStatus.DEFAULT,
  fitToContentHeight: true,
  operations: [],
  isLocationSelected: true,
  menus: [],
  menuAvailabilityById: {},
  menuAvailabilityStatusById: {},
  menuNextAvailabilityById: {},
  sectionAvailabilityById: {},
  sectionAvailabilityStatusById: {},
  itemMap: {},
  cartQuantities: {},
  fetchOperationsStatus: RequestStatus.DEFAULT,
  fetchMenusStatus: RequestStatus.DEFAULT,
  fetchItemsStatus: RequestStatus.DEFAULT,
  fulfillmentMethods: [],
  fetchFulfillmentMethodsStatus: RequestStatus.DEFAULT,
  currency: '',
  timeSlots: [],
  fetchTimeSlotsStatus: RequestStatus.DEFAULT,
  timeSlotsByOperationId: {},
  fetchTimeSlotsByOperationStatus: RequestStatus.DEFAULT,
  acceptingDispatchTypesByOperationId: {},
  dispatchesInfo: {},
  hasError: false,
  coverImage: undefined,
  fetchCoverImageStatus: RequestStatus.DEFAULT,
  fetchOperations: noop,
  fetchMenus: noop,
  getCachedOperationMenus: () => undefined,
  updateMenuAvailabilityStatus: () => Promise.resolve(undefined),
  fetchItems: noop,
  fetchFulfillmentMethods: noop,
  fetchTimeSlots: noop,
  fetchTimeSlotsByOperation: noop,
  fetchIsPremium: () => Promise.resolve(false),
  fetchCoverImage: noop,
  setIsLocationSelected: noop,
  setSelectedDispatchType: noop,
  setDispatchesInfo: noop,
  setAcceptingDispatchTypesByOperationId: noop,
  onItemClick: noop,
  openCart: noop,
  openFulfillmentModal: noop,
};

const { withStorageProvider, useStorage } = createStorageProvider(
  defaultOnlineOrdersStorage,
);

const initOnlineOrdersStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
): OnlineOrdersStorage => {
  const timezone = getSiteTimezone(flowAPI);
  const timezoneOffset = getTimezoneOffset(timezone);
  const currency = getSiteCurrency(flowAPI);
  const { metaSiteId } = getInstance(flowAPI);
  const { setProps } = controllerConfig;

  const fetchOperations = async (
    flowApi: ControllerFlowAPI,
    operationGroupId?: string,
  ) => {
    const operations = operationGroupId
      ? await operationsService.getOperationsByGroupId(
          flowApi.httpClient,
          flowApi.errorHandler.withErrorHandler,
          operationGroupId,
        )
      : await operationsService.listOperations(
          flowApi.httpClient,
          flowApi.errorHandler.withErrorHandler,
        );

    return operations ?? [];
  };

  let operationMenusCache:
    | { operationId: string; menus: PopulatedMenu[] }
    | undefined;

  const fetchMenus = async (
    flowApi: ControllerFlowAPI,
    operationId: string,
  ) => {
    const availability = await menuService.getMenusAvailabilityByOperation(
      flowApi,
      operationId,
      timezoneOffset,
    );
    const menuIds = Object.keys(availability ?? {});
    const populatedMenus = await menuService.getPopulatedMenus(
      flowApi,
      menuIds,
      timezone,
    );

    operationMenusCache = {
      operationId,
      menus: populatedMenus ?? [],
    };

    setProps({
      menuAvailabilityById: availability ?? {},
      menuAvailabilityStatusById: {},
      menuNextAvailabilityById: {},
      sectionAvailabilityById: {},
      sectionAvailabilityStatusById: {},
    });

    return operationMenusCache.menus;
  };

  const getCachedOperationMenus = (
    operationId: string,
  ): PopulatedMenu[] | undefined =>
    operationMenusCache?.operationId === operationId
      ? operationMenusCache.menus
      : undefined;

  const buildMenuNextAvailabilityById = (
    timeSlotsPerMenu: TimeSlotsPerMenu,
    dispatchType?: DispatchType,
  ): Record<string, boolean> =>
    Object.entries(timeSlotsPerMenu).reduce<Record<string, boolean>>(
      (acc, [menuId, timeSlotsByFulfillment]) => {
        const fulfillmentType = dispatchType?.toLowerCase();

        acc[menuId] = fulfillmentType
          ? !!timeSlotsByFulfillment[fulfillmentType]
          : Object.keys(timeSlotsByFulfillment).length > 0;

        return acc;
      },
      {},
    );

  const buildSectionAvailabilityMetadata = (
    menus: PopulatedMenu[],
    timeSlot: AvailabilityTimeSlot,
  ): Pick<
    OnlineOrdersData,
    'sectionAvailabilityById' | 'sectionAvailabilityStatusById'
  > => {
    const sectionAvailabilityById: Partial<
      Record<string, MenuSectionAvailability>
    > = {};
    const sectionAvailabilityStatusById: SectionAvailabilityStatusById = {};

    menus.forEach((menu) => {
      menu.sections.forEach((section) => {
        const availability = section.orderSettings?.availability;

        if (availability) {
          sectionAvailabilityById[section.id] = availability;
        }

        sectionAvailabilityStatusById[section.id] =
          getAvailabilityStatusForTimeSlot({
            availability,
            selectedTimeSlot: timeSlot,
            timezone,
          });
      });
    });

    return {
      sectionAvailabilityById,
      sectionAvailabilityStatusById,
    };
  };

  const updateMenuAvailabilityStatus = async (
    flowApi: ControllerFlowAPI,
    operationId: string,
    timeSlot?: AvailabilityTimeSlot,
    options?: {
      dispatchType?: DispatchType;
      deliveryAddress?: Address;
    },
  ): Promise<PopulatedMenu[] | undefined> => {
    if (
      !operationMenusCache ||
      operationMenusCache.operationId !== operationId
    ) {
      setProps({
        menus: [],
        menuAvailabilityStatusById: {},
        menuNextAvailabilityById: {},
        sectionAvailabilityById: {},
        sectionAvailabilityStatusById: {},
      });

      return undefined;
    }

    const operationMenus = operationMenusCache.menus;

    if (!timeSlot) {
      setProps({
        menus: operationMenus,
        menuAvailabilityStatusById: {},
        menuNextAvailabilityById: {},
        sectionAvailabilityById: {},
        sectionAvailabilityStatusById: {},
      });

      return operationMenus;
    }

    let menuAvailabilityStatusById: MenuAvailabilityStatusById = {};
    let timeSlotsPerMenu: TimeSlotsPerMenu | undefined;

    try {
      const metadata = await ordersSettingsService.getMenuAvailabilityMetadata(
        flowApi,
        {
          operationId,
          timeSlot,
          deliveryAddress: options?.deliveryAddress,
        },
      );

      menuAvailabilityStatusById = metadata.menuAvailabilityStatusById;
      timeSlotsPerMenu = metadata.timeSlotsPerMenu;
    } catch {
      flowApi.panoramaClient
        .errorMonitor()
        .reportError(new Error('updateMenuAvailabilityStatus metadata failed'));
    }

    if (
      operationMenusCache?.operationId !== operationId ||
      operationMenusCache.menus !== operationMenus
    ) {
      return undefined;
    }

    setProps({
      menus: operationMenus,
      menuAvailabilityStatusById,
      menuNextAvailabilityById: timeSlotsPerMenu
        ? buildMenuNextAvailabilityById(timeSlotsPerMenu, options?.dispatchType)
        : {},
      ...buildSectionAvailabilityMetadata(operationMenus, timeSlot),
    });

    return operationMenus;
  };

  const fetchItems = async (flowApi: ControllerFlowAPI, itemIds: string[]) => {
    const items = await itemService.getItems(
      flowApi,
      itemIds,
      currency,
      metaSiteId,
      timezone,
    );
    const itemMap: Record<string, MenuItem> = {};

    items.forEach((item) => {
      if (item.id) {
        itemMap[item.id] = item;
      }
    });

    return itemMap;
  };

  const fetchTimeSlots = (
    flowApi: ControllerFlowAPI,
    operation: Operation,
    deliveryAddress?: Address,
  ): Promise<TimeSlot[]> =>
    fulfillmentsService.fetchFirstAvailableTimeSlot(
      flowApi,
      operation.id,
      deliveryAddress,
    );

  const fetchTimeSlotsByOperation = (
    flowApi: ControllerFlowAPI,
    operationIds: string[],
    deliveryAddress?: Address,
  ): Promise<Record<string, TimeSlot[]>> =>
    fulfillmentsService.fetchFirstAvailableTimeSlotsPerOperation(
      flowApi,
      operationIds,
      deliveryAddress,
    );

  return {
    ...defaultOnlineOrdersStorage,
    currency,
    timezone,
    regionalSettings: getRegionalSettings(flowAPI),
    siteLocale: getSiteLocale(flowAPI),
    fetchOperations: wrapRequest(
      flowAPI,
      fetchOperations,
      'operations',
      'fetchOperationsStatus',
    ),
    fetchMenus: wrapRequest(flowAPI, fetchMenus, 'menus', 'fetchMenusStatus'),
    getCachedOperationMenus,
    updateMenuAvailabilityStatus: (operationId, timeSlot, options) =>
      updateMenuAvailabilityStatus(flowAPI, operationId, timeSlot, options),
    fetchItems: wrapRequest(flowAPI, fetchItems, 'itemMap', 'fetchItemsStatus'),
    fetchFulfillmentMethods: wrapRequest(
      flowAPI,
      (flowApi, fulfillmentIds) =>
        fulfillmentsService.fetchAllFulfillments(flowApi, fulfillmentIds),
      'fulfillmentMethods',
      'fetchFulfillmentMethodsStatus',
    ),
    fetchTimeSlots: wrapRequest(
      flowAPI,
      fetchTimeSlots,
      'timeSlots',
      'fetchTimeSlotsStatus',
    ),
    fetchTimeSlotsByOperation: wrapRequest(
      flowAPI,
      fetchTimeSlotsByOperation,
      'timeSlotsByOperationId',
      'fetchTimeSlotsByOperationStatus',
    ),
    fetchIsPremium: wrapRequest(
      flowAPI,
      premiumService.getIsPremium,
      'isPremium',
      'fetchIsPremiumStatus',
    ),
    fetchCoverImage: wrapRequest(
      flowAPI,
      getCoverImage,
      'coverImage',
      'fetchCoverImageStatus',
    ),
    setIsLocationSelected: (value) => setProps({ isLocationSelected: value }),
    setSelectedDispatchType: (type) => setProps({ selectedDispatchType: type }),
    setDispatchesInfo: (dispatchesInfo) => setProps({ dispatchesInfo }),
    setAcceptingDispatchTypesByOperationId: (map) =>
      setProps({ acceptingDispatchTypesByOperationId: map }),
  };
};

const mockOnlineOrdersStorage = (
  flowAPI: ControllerFlowAPI,
  controllerConfig: IWidgetControllerConfig,
): OnlineOrdersStorage => {
  const {
    setIsLocationSelected,
    setSelectedDispatchType,
    setDispatchesInfo,
    setAcceptingDispatchTypesByOperationId,
  } = initOnlineOrdersStorage(flowAPI, controllerConfig);

  return {
    ...getDefaultMockOnlineOrdersStorage(),
    setIsLocationSelected,
    setSelectedDispatchType,
    setDispatchesInfo,
    setAcceptingDispatchTypesByOperationId,
    cartQuantities: { 'mock-item': 1 },
    fetchOperations: wrapRequest(
      flowAPI,
      getOperationsMock,
      'operations',
      'fetchOperationsStatus',
    ),
    fetchMenus: wrapRequest(flowAPI, getMenusMock, 'menus', 'fetchMenusStatus'),
    getCachedOperationMenus: () => getMockMenus(),
    updateMenuAvailabilityStatus: (operationId) =>
      getMenusMock(flowAPI, operationId),
    fetchItems: wrapRequest(
      flowAPI,
      getItemsMock,
      'itemMap',
      'fetchItemsStatus',
    ),
    fetchFulfillmentMethods: wrapRequest(
      flowAPI,
      getFulfillmentMethodsMock,
      'fulfillmentMethods',
      'fetchFulfillmentMethodsStatus',
    ),
    fetchTimeSlots: wrapRequest(
      flowAPI,
      getTimeSlotsMock,
      'timeSlots',
      'fetchTimeSlotsStatus',
    ),
    fetchTimeSlotsByOperation: wrapRequest(
      flowAPI,
      getTimeSlotsPerOperationMock,
      'timeSlotsByOperationId',
      'fetchTimeSlotsByOperationStatus',
    ),
    fetchCoverImage: wrapRequest(
      flowAPI,
      getCoverImage,
      'coverImage',
      'fetchCoverImageStatus',
    ),
  };
};

export {
  initOnlineOrdersStorage,
  mockOnlineOrdersStorage,
  withStorageProvider as withOnlineOrdersStorageProvider,
  useStorage as useOnlineOrdersStorage,
};
